/**
 * Almode Chat (suporte) no ERP: tipos do contrato e regras puras do chat nativo.
 *
 * O contrato é o da API do suporte (https://api.suporte.almode.app/chat/v1, repositório almode-suporte,
 * lib/chat-client.ts e server/chat/service.ts). As regras de tela vêm do app Almode, que é a referência de
 * comportamento (lib/support-chat-utils.ts, components/support-chat-bubble.tsx e lib/support-chat-rich-text.ts).
 *
 * Este arquivo não importa nada pelo alias `@/` de propósito, para poder ser testado direto pelo jest.
 */

export type InterativoDoChat =
	| { kind: 'buttons'; buttons: { id: string; title: string }[] }
	| { kind: 'list'; buttonText: string; rows: { id: string; title: string; description?: string }[] }

export type MensagemDoChat = {
	id: number
	from: 'cliente' | 'robo' | 'atendente'
	authorName: string | null
	type: string
	text: string | null
	caption: string | null
	media: { url: string; mime: string | null; filename: string | null } | null
	interactive: InterativoDoChat | null
	status: string | null
	sentAt: string
}

export type EstadoDoChat = {
	typing?: boolean
	conversationId: number | null
	status: string | null
	inTriage: boolean
	waitingAgent: boolean
	waitingText?: string | null
	agentName: string | null
	actionableMessageId: number | null
}

export type RespostaDeMensagens = {
	items: MensagemDoChat[]
	cursor: string | null
	unread: number
	state: EstadoDoChat
}

export type SessaoDoChat = {
	sessionToken: string
	expiresAt: string
	name: string
	storeName: string | null
	product?: string
}

/** Sessão que vence em menos de 1 minuto já não serve: venceria no meio da conversa. */
export const FOLGA_DA_SESSAO_MS = 60_000
export const POLLING_PADRAO_MS = 3_000
/** Botão do copiloto que pede print da tela. No navegador vira o seletor de arquivo, como no widget do suporte. */
export const ID_DO_BOTAO_DE_PRINT = 'cop_print'
/** O suporte aceita imagem e PDF de até 10 MB (áudio só é tocado aqui, não gravado). */
export const TIPOS_DE_ANEXO = ['image/jpeg', 'image/png', 'application/pdf']
export const TAMANHO_MAXIMO_DO_ANEXO = 10 * 1024 * 1024
/**
 * O que o cliente vê quando o erro do sistema vai anexado: o texto padrão do suporte (DEFAULT_ERROR_CONTEXT do Almode
 * Chat). Código, mensagem e stack ficam no anexo, que só o atendente vê.
 */
export const TEXTO_DO_ERRO_ANEXADO = 'Ocorreu um erro no sistema e preciso de ajuda.'

/**
 * Recusa do suporte ao anexo do erro: não existe, é de outro usuário, já foi usado ou venceu. Tentar de novo não
 * adianta, então a mensagem sai da conversa e o aviso pede para a pessoa descrever o que aconteceu.
 */
export function erroAnexadoRecusado(status: number): boolean {
	return [400, 404, 409, 410].includes(status)
}

/** Aviso no diálogo do erro quando o backend do ERP não conseguiu registrar o erro no suporte. */
export function falhaAoAnexarErro(status: number | null | undefined): string {
	if (status === 404) {
		return 'Não encontramos os detalhes deste erro. Abra o chat pelo ícone da barra e informe o código acima.'
	}
	if (status === 503) {
		return 'O chat de suporte não está disponível neste ambiente. Informe o código acima ao suporte.'
	}
	return 'Não foi possível enviar o erro ao suporte agora. Tente de novo ou informe o código acima no chat.'
}

export function sessaoViva(token: string | null, expiresAt: string | null, agora = Date.now()): boolean {
	return !!token && !!expiresAt && new Date(expiresAt).getTime() > agora + FOLGA_DA_SESSAO_MS
}

/** O suporte devolve `conversationId` nulo depois que a conversa vira "resolvida". */
export function conversaAberta(estado: EstadoDoChat | null | undefined): boolean {
	return !!estado && estado.conversationId != null && estado.status !== 'resolvida'
}

/** Junta o que veio do polling por id (a versão nova substitui a antiga), em ordem de id. */
export function juntarMensagens<T extends { id: number }>(atuais: T[], novas: T[]): T[] {
	if (!novas.length) return atuais
	const porId = new Map(atuais.map(mensagem => [mensagem.id, mensagem]))
	novas.forEach(mensagem => porId.set(mensagem.id, mensagem))
	return [...porId.values()].sort((a, b) => a.id - b.id)
}

/** Id da última mensagem do suporte que ainda dá para marcar como lida. */
export function ultimaMensagemLegivel(itens: Pick<MensagemDoChat, 'id' | 'from' | 'status'>[]): number {
	return itens.reduce(
		(maior, mensagem) =>
			mensagem.from !== 'cliente' && (mensagem.status === 'sent' || mensagem.status === 'delivered') && mensagem.id > maior
				? mensagem.id
				: maior,
		0,
	)
}

/** Painel aberto: o intervalo do suporte, dobrando a cada falha seguida até 30 s. */
export function atrasoDoPolling(baseMs: number, falhas: number): number {
	if (falhas <= 0) return baseMs
	return Math.min(30_000, baseMs * 2 ** Math.min(falhas, 4))
}

export function ultimaMensagemDoSuporte(itens: MensagemDoChat[]): MensagemDoChat | null {
	for (let i = itens.length - 1; i >= 0; i -= 1) {
		if (itens[i].from !== 'cliente') return itens[i]
	}
	return null
}

/** Primeiras ~90 letras, cortadas em palavra inteira e com reticências. O texto inteiro está a um clique. */
export function trechoDaMensagem(texto: string, maximo = 90): string {
	const limpo = texto.replace(/\s+/g, ' ').trim()
	if (limpo.length <= maximo) return limpo
	const corte = limpo.slice(0, maximo)
	const ultimoEspaco = corte.lastIndexOf(' ')
	return `${(ultimoEspaco > 40 ? corte.slice(0, ultimoEspaco) : corte).replace(/[\s.,;:!?-]+$/, '')}…`
}

export type PreviaDoChat = { id: number; autor: string; texto: string }

export function previaDaMensagem(mensagem: MensagemDoChat): PreviaDoChat {
	const autor = mensagem.authorName || (mensagem.from === 'robo' ? 'Assistente Almode' : 'Atendimento Almode')
	const corpo = (mensagem.text || mensagem.caption || '').trim()
	const texto = corpo
		? trechoDaMensagem(corpo)
		: mensagem.type === 'image'
			? 'Enviou uma imagem'
			: ehAudio(mensagem)
				? 'Enviou um áudio'
				: 'Enviou um arquivo'
	return { id: mensagem.id, autor, texto }
}

/** Selo de não lidas: "9+" acima de 9. */
export function rotuloDeNaoLidas(quantidade: number): string {
	return quantidade > 9 ? '9+' : String(quantidade)
}

export function rotuloDaBolha(naoLidas: number, digitando: boolean, atendente: string | null): string {
	const partes = ['Atendimento Almode']
	if (naoLidas > 0) partes.push(`${naoLidas} ${naoLidas === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`)
	if (digitando) partes.push('atendente digitando')
	if (atendente) partes.push(`atendido por ${atendente}`)
	return partes.join(', ')
}

export function ehAudio(mensagem: Pick<MensagemDoChat, 'type' | 'media'>): boolean {
	return mensagem.type === 'audio'
		|| (!!mensagem.media && mensagem.type !== 'image' && (mensagem.media.mime || '').toLowerCase().startsWith('audio/'))
}

// O suporte e o app mostram data e hora sempre em BRT (UTC-3 fixo), independente do fuso da máquina.
const OFFSET_BRT_MS = 3 * 60 * 60 * 1000
const DIA_MS = 24 * 60 * 60 * 1000
const doisDigitos = (n: number) => String(n).padStart(2, '0')
const relogioBrt = (data: Date | string | number) => new Date(new Date(data).getTime() - OFFSET_BRT_MS)
const chaveDoDiaBrt = (data: Date | string | number) => relogioBrt(data).toISOString().slice(0, 10)

/** "14:05" em BRT. */
export function horaDaMensagem(data: Date | string | number): string {
	const brt = relogioBrt(data)
	return `${doisDigitos(brt.getUTCHours())}:${doisDigitos(brt.getUTCMinutes())}`
}

/** "Hoje", "Ontem" ou "DD-MM-AAAA" em BRT. */
export function diaDaMensagem(data: Date | string | number, agora = Date.now()): string {
	const chave = chaveDoDiaBrt(data)
	if (chave === chaveDoDiaBrt(agora)) return 'Hoje'
	if (chave === chaveDoDiaBrt(agora - DIA_MS)) return 'Ontem'
	const brt = relogioBrt(data)
	return `${doisDigitos(brt.getUTCDate())}-${doisDigitos(brt.getUTCMonth() + 1)}-${brt.getUTCFullYear()}`
}

/** Subtítulo do cabeçalho e faixa de espera (o texto da faixa vem pronto do suporte, com o horário oficial). */
export function situacaoDoAtendimento(estado: EstadoDoChat | null): { subtitulo: string; faixa: string | null } {
	if (!estado || estado.conversationId == null) return { subtitulo: 'Atendimento pelo chat', faixa: null }
	if (estado.agentName) return { subtitulo: `${estado.agentName} está te atendendo`, faixa: null }
	if (estado.waitingAgent) {
		return {
			subtitulo: 'Aguardando um atendente',
			faixa: (estado.waitingText || '').trim() || 'Um atendente vai responder em breve. Você pode continuar escrevendo.',
		}
	}
	return { subtitulo: 'Atendimento pelo chat', faixa: null }
}

export function opcoesDaMensagem(mensagem: MensagemDoChat): { id: string; title: string; description?: string }[] {
	if (!mensagem.interactive) return []
	return mensagem.interactive.kind === 'buttons' ? mensagem.interactive.buttons : mensagem.interactive.rows
}

/** Opção escolhida numa mensagem de botões: a primeira resposta do cliente logo depois dela, se bater. */
export function opcaoEscolhida(itens: MensagemDoChat[], indice: number): string | null {
	const mensagem = itens[indice]
	if (!mensagem || !mensagem.interactive) return null
	const resposta = itens.slice(indice + 1).find(item => item.from === 'cliente')
	const texto = resposta && resposta.text ? resposta.text.trim().toLowerCase() : ''
	if (!texto) return null
	const escolhida = opcoesDaMensagem(mensagem).find(opcao => opcao.title.trim().toLowerCase() === texto)
	return escolhida ? escolhida.title : null
}

// ─── Texto rico das bolhas ────────────────────────────────────────────────────────────────────────────
// Links (http, https e www) e a formatação do WhatsApp (*negrito*, _itálico_, ~riscado~), só quando o marcador
// abre e fecha na mesma linha, encostado na palavra: "2 * 3" e snake_case não viram formatação. Os links saem
// antes da formatação, então o "__" de uma URL nunca vira itálico.

export type EstiloDoTexto = { negrito?: boolean; italico?: boolean; riscado?: boolean }
export type TrechoDoTexto =
	| ({ tipo: 'texto'; texto: string } & EstiloDoTexto)
	| ({ tipo: 'link'; texto: string; url: string } & EstiloDoTexto)

const URL_NO_TEXTO = /(?:https?:\/\/|www\.)[^\s<>"]+/gi
const PONTUACAO_FINAL = '.,;:!?\'"*_~…'
const PARES: Record<string, string> = { ')': '(', ']': '[', '}': '{' }
const LETRA_OU_NUMERO = /[\p{L}\p{N}]/u
// Um caractere da área de uso privado por link enquanto a formatação é lida.
const MARCADOR_DE_LINK = 0xe000
const MARCADORES: Record<string, keyof EstiloDoTexto> = { '*': 'negrito', _: 'italico', '~': 'riscado' }

const ocorrencias = (texto: string, caractere: string) => texto.split(caractere).length - 1
const ehEspaco = (c: string | undefined) => c === undefined || /\s/.test(c)
const ehLetra = (c: string | undefined) => c !== undefined && LETRA_OU_NUMERO.test(c)

function aparaUrl(bruta: string): string {
	let url = bruta
	for (;;) {
		const ultimo = url.slice(-1)
		if (!ultimo) return url
		if (PONTUACAO_FINAL.includes(ultimo)) {
			url = url.slice(0, -1)
			continue
		}
		const abre = PARES[ultimo]
		if (abre && ocorrencias(url, ultimo) > ocorrencias(url, abre)) {
			url = url.slice(0, -1)
			continue
		}
		return url
	}
}

function separaLinks(texto: string): ({ tipo: 'texto'; texto: string } | { tipo: 'link'; texto: string; url: string })[] {
	const saida: ({ tipo: 'texto'; texto: string } | { tipo: 'link'; texto: string; url: string })[] = []
	let ultimo = 0
	URL_NO_TEXTO.lastIndex = 0
	for (let achado = URL_NO_TEXTO.exec(texto); achado; achado = URL_NO_TEXTO.exec(texto)) {
		const inicio = achado.index
		if (inicio > 0 && ehLetra(texto[inicio - 1])) continue
		const link = aparaUrl(achado[0])
		if (!/[\p{L}\p{N}]\.[\p{L}\p{N}]|\/\/[\p{L}\p{N}]/u.test(link) || /^www\.?$/i.test(link)) continue
		if (inicio > ultimo) saida.push({ tipo: 'texto', texto: texto.slice(ultimo, inicio) })
		saida.push({ tipo: 'link', texto: link, url: /^www\./i.test(link) ? `https://${link}` : link })
		ultimo = inicio + link.length
		URL_NO_TEXTO.lastIndex = ultimo
	}
	if (ultimo < texto.length) saida.push({ tipo: 'texto', texto: texto.slice(ultimo) })
	return saida
}

type Corrida = { texto: string } & EstiloDoTexto

function formata(texto: string, estilo: EstiloDoTexto, saida: Corrida[]) {
	let acumulado = ''
	const descarrega = () => {
		if (acumulado) saida.push({ texto: acumulado, ...estilo })
		acumulado = ''
	}
	let i = 0
	while (i < texto.length) {
		const caractere = texto[i]
		const chave = MARCADORES[caractere]
		if (chave && !estilo[chave] && !ehLetra(texto[i - 1]) && !ehEspaco(texto[i + 1])) {
			let fecha = -1
			for (let k = i + 2; k < texto.length; k += 1) {
				if (texto[k] === '\n') break
				if (texto[k] === caractere && !ehEspaco(texto[k - 1]) && !ehLetra(texto[k + 1])) {
					fecha = k
					break
				}
			}
			if (fecha > 0) {
				descarrega()
				formata(texto.slice(i + 1, fecha), { ...estilo, [chave]: true }, saida)
				i = fecha + 1
				continue
			}
		}
		acumulado += caractere
		i += 1
	}
	descarrega()
}

export function textoRico(texto: string | null | undefined): TrechoDoTexto[] {
	if (!texto) return []
	const links: { texto: string; url: string }[] = []
	let plano = ''
	separaLinks(texto).forEach(pedaco => {
		if (pedaco.tipo === 'link') {
			plano += String.fromCharCode(MARCADOR_DE_LINK + links.length)
			links.push({ texto: pedaco.texto, url: pedaco.url })
		} else {
			plano += pedaco.texto
		}
	})
	const corridas: Corrida[] = []
	formata(plano, {}, corridas)

	const trechos: TrechoDoTexto[] = []
	corridas.forEach(({ texto: conteudo, ...estilo }) => {
		let acumulado = ''
		for (const caractere of conteudo) {
			const indice = caractere.charCodeAt(0) - MARCADOR_DE_LINK
			if (caractere.length === 1 && indice >= 0 && indice < links.length) {
				if (acumulado) trechos.push({ tipo: 'texto', texto: acumulado, ...estilo })
				acumulado = ''
				trechos.push({ tipo: 'link', ...links[indice], ...estilo })
			} else {
				acumulado += caractere
			}
		}
		if (acumulado) trechos.push({ tipo: 'texto', texto: acumulado, ...estilo })
	})
	return trechos
}

export type Caixa = { top: number; left: number; width: number; height: number }

/**
 * Balão do destaque: embaixo do ícone, com a seta no centro dele. O ícone fica perto da direita da barra, então o
 * balão cresce para a esquerda, sem sair da tela.
 */
export function posicaoDoBalaoDeNovidade(
	icone: Caixa,
	larguraDaTela: number,
	larguraDesejada = 320,
	margem = 12,
): { top: number; left: number; largura: number; seta: number } {
	const largura = Math.min(larguraDesejada, larguraDaTela - 2 * margem)
	const centro = icone.left + icone.width / 2
	const left = Math.min(Math.max(margem, centro - largura + 32), larguraDaTela - largura - margem)
	return {
		top: Math.round(icone.top + icone.height + 14),
		left: Math.round(left),
		largura: Math.round(largura),
		seta: Math.round(Math.min(Math.max(centro - left, 20), largura - 20)),
	}
}

/** Deslocamento e escala que levam o painel, pelo centro, até onde ele reabre (a bolha ou o ícone da barra). */
export function encolherAte(painel: Caixa, destino: Caixa): { x: number; y: number; escalaX: number; escalaY: number } {
	return {
		x: destino.left + destino.width / 2 - (painel.left + painel.width / 2),
		y: destino.top + destino.height / 2 - (painel.top + painel.height / 2),
		escalaX: painel.width > 0 ? destino.width / painel.width : 1,
		escalaY: painel.height > 0 ? destino.height / painel.height : 1,
	}
}
